import React, { Component, ErrorInfo, ReactNode } from 'react';

import { config, isProduction } from '@/config/environment';
import { getErrorReporting } from '@/services/error-reporting';

import { showToast } from './toast';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorId: Date.now().toString(),
      retryCount: 0,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Use the error reporting service
    const errorReporting = getErrorReporting();
    if (errorReporting) {
      errorReporting.reportError(error, errorInfo, {
        component: 'ErrorBoundary',
        action: 'componentDidCatch',
        props: this.props.resetKeys,
      });
    } else if (isProduction()) {
      this.logErrorToService(error, errorInfo);
    }

    // Show toast notification for error
    showToast(
      'Something went wrong. Please try again or refresh the page.',
      false,
      {
        position: 'top-center',
      }
    );

    // Auto-refresh in production after 5 seconds if retry count is high
    if (isProduction() && this.state.retryCount >= 2) {
      this.resetTimeoutId = setTimeout(() => {
        window.location.reload();
      }, 5000);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state when resetKeys change
    if (
      hasError &&
      resetOnPropsChange &&
      resetKeys &&
      prevProps.resetKeys &&
      resetKeys.some((key, index) => prevProps.resetKeys![index] !== key)
    ) {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        errorId: undefined,
      });
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private logErrorToService(error: Error, errorInfo: ErrorInfo) {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: localStorage.getItem(config.auth.tokenKey)
        ? 'authenticated'
        : 'anonymous',
      errorId: this.state.errorId,
      retryCount: this.state.retryCount,
      props: this.props.resetKeys,
    };

    // In production, you would send this to an error tracking service
    return errorData;
  }

  private handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: undefined,
      retryCount: prevState.retryCount + 1,
    }));
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleReportError = () => {
    const { error, errorInfo } = this.state;
    if (error) {
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      };

      // Copy error report to clipboard
      navigator.clipboard
        .writeText(JSON.stringify(errorReport, null, 2))
        .then(() => {
          showToast('Error report copied to clipboard', true, {
            position: 'top-center',
          });
        })
        .catch(() => {
          showToast('Failed to copy error report', false, {
            position: 'top-center',
          });
        });
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='min-h-screen flex items-center justify-center bg-gray-5 px-4'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center'>
            <div className='w-16 h-16 mx-auto mb-4 bg-destructive-10 rounded-full flex items-center justify-center'>
              <svg
                className='w-8 h-8 text-destructive-60'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>

            <h2 className='text-xl font-semibold text-gray-90 mb-2'>
              Oops! Something went wrong
            </h2>

            <p className='text-gray-60 mb-6'>
              We're sorry for the inconvenience. The application encountered an
              unexpected error.
            </p>

            {this.state.retryCount > 0 && (
              <div className='mb-4 p-3 bg-warning-5 border border-warning-20 rounded-md'>
                <p className='text-sm text-warning-70'>
                  Retry attempt: {this.state.retryCount}
                </p>
              </div>
            )}

            <div className='space-y-3'>
              <button
                onClick={this.handleRetry}
                className='w-full bg-brand-60 text-white px-4 py-2 rounded-lg hover:bg-brand-70 transition-colors'
              >
                Try Again
              </button>

              <button
                onClick={this.handleRefresh}
                className='w-full bg-gray-30 text-gray-70 px-4 py-2 rounded-lg hover:bg-gray-40 transition-colors'
              >
                Refresh Page
              </button>

              <button
                onClick={this.handleReportError}
                className='w-full bg-gray-10 text-gray-60 px-4 py-2 rounded-lg hover:bg-gray-20 transition-colors text-sm'
              >
                Copy Error Report
              </button>
            </div>

            {!isProduction() && this.state.error && (
              <details className='mt-6 text-left'>
                <summary className='cursor-pointer text-sm text-gray-50 hover:text-gray-70'>
                  Error Details (Development Only)
                </summary>
                <div className='mt-2 p-3 bg-gray-10 rounded text-xs font-mono text-gray-70 max-h-32 overflow-auto'>
                  <div className='mb-2'>
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                  <div className='mb-2'>
                    <strong>Error ID:</strong> {this.state.errorId}
                  </div>
                  <div className='mb-2'>
                    <strong>Retry Count:</strong> {this.state.retryCount}
                  </div>
                  {this.state.error.stack && (
                    <div>
                      <strong>Stack:</strong>
                      <pre className='whitespace-pre-wrap text-xs'>
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: ErrorInfo) => void,
  resetKeys?: Array<string | number>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary
      fallback={fallback}
      onError={onError}
      resetOnPropsChange={true}
      resetKeys={resetKeys}
    >
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${
    Component.displayName || Component.name
  })`;
  return WrappedComponent;
}

export function useErrorHandler() {
  return (error: Error, errorInfo?: any) => {
    if (isProduction()) {
      const errorData = getErrorReporting()?.reportError(error, errorInfo, {
        component: 'useErrorHandler',
        action: 'useErrorHandler',
        props: errorInfo,
      });
      return errorData;
    }

    showToast('An error occurred. Please try again.', false, {
      position: 'top-right',
    });
  };
}

export default ErrorBoundary;
